import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../utils/api'; // Ensure your API client (e.g., axios instance) is configured

// Async thunk to fetch USB Drives from your Django backend.
// Adjust the URL to match your backend API endpoint, e.g., `/api/usb-drives/`
export const fetchUsbDrives = createAsyncThunk(
  'usbDrives/fetchUsbDrives',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/usb-drives/');
      return response.data; // Expected: an array of product objects
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch USB Drives products'
      );
    }
  }
);

// Initial filter state (customize values as needed)
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false }, // Adjust if different product types apply
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

// The initial state starts with an empty product list (to be populated via the API)
const initialState = {
  loading: false,
  error: null,
  products: [],
  filteredProducts: [],
  filters: initialFilters,
};

const usbDrivesSlice = createSlice({
  name: 'usbDrives',
  initialState,
  reducers: {
    // Update a top-level boolean filter (e.g., "personalize")
    setSimpleFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update a nested filter property (for example, within productTypes, sizes, colors, etc.)
    setNestedFilter(state, action) {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // Update a radio filter (e.g., orientation, corners)
    setRadioFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update the range filter (price range)
    setRangeFilter(state, action) {
      state.filters.priceRange = action.payload;
    },
    // Reset all filters to the default state and show all products
    resetFilters(state) {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
    // Apply filtering logic based on the current filters
    applyFilters(state) {
      const { filters, products } = state;
      
      const selectedTypes = Object.entries(filters.productTypes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      const selectedSizes = Object.entries(filters.sizes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      const selectedRates = Object.entries(filters.rates)
        .filter(([_, value]) => value)
        .map(([key]) => {
          if (key === 'five') return 5;
          if (key === 'four') return 4;
          if (key === 'three') return 3;
          return null;
        })
        .filter((rate) => rate !== null);
      
      state.filteredProducts = products.filter((product) => {
        // Filter by product type (if any selected)
        if (selectedTypes.length > 0) {
          // Map product.type to a key. Adjust if your API returns a different type format.
          const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
          if (!selectedTypes.includes(prodTypeKey)) return false;
        }
        // Filter by sizes (if any are selected)
        if (selectedSizes.length > 0) {
          if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
        }
        // Price range filter
        if (product.price > filters.priceRange) return false;
        // Rating filter
        if (selectedRates.length > 0) {
          if (!selectedRates.includes(product.rating)) return false;
        }
        // Additional filters (e.g., colors, fabric) can be added here as needed
        return true;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsbDrives.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsbDrives.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.filteredProducts = action.payload;
      })
      .addCase(fetchUsbDrives.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSimpleFilter, 
  setNestedFilter, 
  setRadioFilter, 
  setRangeFilter, 
  resetFilters, 
  applyFilters 
} = usbDrivesSlice.actions;

export default usbDrivesSlice.reducer;
